<script lang="ts">
  import { Collapsible } from 'bits-ui';
  import PushPinIcon from 'phosphor-svelte/lib/PushPinIcon';
  import PushPinSlashIcon from 'phosphor-svelte/lib/PushPinSlashIcon';

  import type { MemberView, TimelineItemView } from '#src/generated/protocol';

  import { i18n } from '#lib/i18n.js';
  import { reactionKey } from '#lib/model/model.js';
  import { preferences } from '#lib/settings/preferences.svelte.js';
  import MediaImage from '#lib/ui/MediaImage.svelte';
  import Tooltip from '#lib/ui/primitives/Tooltip.svelte';

  import EditDiff from '../messages/EditDiff.svelte';
  import EmoteCard from '../messages/EmoteCard.svelte';
  import EventTargetPreview from '../messages/EventTargetPreview.svelte';
  import { isCustomReaction } from '../messages/reaction-emote-label';
  import { redactedLabel } from '../messages/redacted-label';
  import { memberName } from '../members/members.js';
  import StateContentDiff from '../messages/StateContentDiff.svelte';
  import StateEventText from './StateEventText.svelte';
  import { stateEventIcon } from './state-event-icon';
  import { reactionSource } from './state-event-text';
  import {
    isEditEvent,
    redactionTarget,
    relationOf,
    type TimelineEventIndex,
  } from './timeline-event-index';
  import { formatDate, formatFullTimestamp, formatMessageTimestamp } from '#lib/ui/date-time.js';
  import UndecryptableNotice from './UndecryptableNotice.svelte';

  const MAX_PIN_PREVIEWS = 4;

  interface Props {
    item: TimelineItemView;
    unreadCount: number;
    roomId?: string;
    events?: TimelineEventIndex;
    members?: readonly MemberView[];
    currentUserId?: string | null;
    onSenderProfile?: (userId: string, anchor: HTMLElement) => void;
    onJumpToEvent?: (eventId: string) => void;
  }

  let {
    item,
    unreadCount,
    roomId = '',
    events,
    members = [],
    currentUserId = null,
    onSenderProfile,
    onJumpToEvent,
  }: Props = $props();
  let peekOpen = $state(false);
  let redactedText = $derived.by(() => {
    const redactor = events?.redactor(item) ?? null;
    return redactedLabel(
      redactor ? memberName(members, redactor) : null,
      item.content.kind === 'redacted' ? item.content.reason : null
    );
  });
  let StateIcon = $derived(stateEventIcon(item));
  let hiddenTarget = $derived.by((): string | null => {
    const content = item.content;
    if (content.kind !== 'hidden_event') return null;
    if (content.event_type === 'm.room.redaction') return redactionTarget(item);
    if (content.event_type === 'm.reaction' || isEditEvent(item)) {
      return relationOf(content.content)?.eventId ?? null;
    }
    return null;
  });
  let reaction = $derived(
    item.content.kind === 'hidden_event' && item.content.event_type === 'm.reaction'
      ? item.content
      : null
  );
  let reactionLabel = $derived(reactionKey(reaction?.content));
  let source = $derived(reactionSource(reaction?.content));
  let customReaction = $derived(source !== null && isCustomReaction(source) ? source : null);
  let customReactionLabel = $derived(
    reactionLabel !== null && !isCustomReaction(reactionLabel)
      ? reactionLabel
      : i18n.t('timeline.customEmote')
  );
  let redactedReaction = $derived(reaction !== null && reactionLabel === null);
  let targetAbove = $derived(preferences.replyPreviewStyle !== 'expanded');
  let pins = $derived.by(() => {
    const content = item.content;
    if (content.kind !== 'state_event' || content.change?.kind !== 'pinned_events') return [];
    return [
      ...content.change.added.map((eventId) => ({ eventId, pinned: true })),
      ...content.change.removed.map((eventId) => ({ eventId, pinned: false })),
    ].slice(0, MAX_PIN_PREVIEWS);
  });
</script>

{#snippet reactionImageContent()}
  {#if customReaction}
    <Tooltip label={customReactionLabel} variant="inline" side="top">
      <MediaImage
        class="state-emote"
        source={customReaction}
        alt={customReactionLabel}
        width={64}
        height={64}
        original
        emoticon
      />
      {#snippet content()}
        <EmoteCard label={customReactionLabel}>
          <MediaImage source={customReaction} alt="" width={64} height={64} original />
        </EmoteCard>
      {/snippet}
    </Tooltip>
  {/if}
{/snippet}

{#snippet targetDetail(target: string)}
  <div class={['state-detail', { 'target-above': targetAbove }]}>
    {#if isEditEvent(item)}
      <EventTargetPreview
        eventId={target}
        {roomId}
        {events}
        {members}
        {currentUserId}
        reply
        onJump={onJumpToEvent}
      >
        {#snippet body()}<EditDiff {item} {roomId} {events} />{/snippet}
      </EventTargetPreview>
    {:else}
      <EventTargetPreview
        eventId={target}
        {roomId}
        {events}
        {members}
        {currentUserId}
        reply
        onJump={onJumpToEvent}
      />
    {/if}
  </div>
{/snippet}

{#snippet stateGutter()}
  <span class="state-icon" aria-hidden="true"><StateIcon /></span>
{/snippet}

{#snippet stateTime()}
  <Tooltip label={formatFullTimestamp(item.timestamp)}>
    {#snippet trigger({ props })}
      <time {...props} class="state-time" datetime={new Date(item.timestamp).toISOString()}>
        {formatMessageTimestamp(item.timestamp)}
      </time>
    {/snippet}
  </Tooltip>
{/snippet}

{#if item.content.kind === 'membership' || item.content.kind === 'profile_change' || (item.content.kind === 'state_event' && item.content.change !== null)}
  <p class="state">
    {@render stateGutter()}
    <StateEventText {item} {members} {onSenderProfile} />
    {@render stateTime()}
  </p>
  {#if pins.length > 0}
    <div class="state-detail">
      {#each pins as pin (pin.eventId)}
        <EventTargetPreview
          eventId={pin.eventId}
          {roomId}
          {events}
          {members}
          {currentUserId}
          icon={pin.pinned ? PushPinIcon : PushPinSlashIcon}
          onJump={onJumpToEvent}
        />
      {/each}
    </div>
  {/if}
{:else if item.content.kind === 'state_event'}
  <p class="state">
    {@render stateGutter()}
    <StateEventText {item} {members} {onSenderProfile} />
    {@render stateTime()}
  </p>
  {#if item.content.content !== null}
    <div class="state-detail">
      <StateContentDiff before={item.content.prev_content} after={item.content.content} />
    </div>
  {/if}
{:else if item.content.kind === 'redacted' || redactedReaction}
  <p class="state redacted">
    {@render stateGutter()}
    <span class="redacted-label state-text">
      {redactedText}
    </span>
    {@render stateTime()}
  </p>
{:else if hiddenTarget !== null}
  {#if targetAbove}{@render targetDetail(hiddenTarget)}{/if}
  <p class="state">
    {@render stateGutter()}
    <StateEventText
      {item}
      {members}
      {onSenderProfile}
      reaction={customReaction ? reactionImageContent : undefined}
    />
    {@render stateTime()}
  </p>
  {#if !targetAbove}{@render targetDetail(hiddenTarget)}{/if}
{:else if item.content.kind === 'hidden_event'}
  {@const raw = item.content.content}
  <div class="debug-event">
    <code>{item.content.event_type}</code>
    <div class="debug-body">
      <span><StateEventText {item} {members} {onSenderProfile} /></span>
      {#if raw !== null}
        <Collapsible.Root bind:open={peekOpen} class="debug-peek-root">
          <Collapsible.Trigger class="debug-peek-trigger">
            {peekOpen ? i18n.t('timeline.hidePeek') : i18n.t('timeline.showPeek')}
          </Collapsible.Trigger>
          <Collapsible.Content class="debug-peek-content">
            <pre class="debug-peek">{JSON.stringify(raw, null, 2)}</pre>
          </Collapsible.Content>
        </Collapsible.Root>
      {/if}
    </div>
    {@render stateTime()}
  </div>
{:else if item.content.kind === 'unable_to_decrypt'}
  <UndecryptableNotice
    id={item.id}
    cause={item.content.reason}
    {roomId}
    sessionId={item.content.session_id}
    sender={item.sender}
    threadRoot={item.thread_root}
  />
{:else if item.content.kind === 'call_invite' || item.content.kind === 'verification_request' || item.content.kind === 'cute' || item.content.kind === 'malformed'}
  <p class="state">
    {@render stateGutter()}
    <StateEventText {item} {members} {onSenderProfile} />
    {@render stateTime()}
  </p>
{:else if item.content.kind === 'unsupported'}
  <p class="state">
    {@render stateGutter()}
    <span class="state-text"
      >{i18n.t('timeline.unsupported', { description: item.content.description })}</span
    >
    {@render stateTime()}
  </p>
{:else if item.content.kind === 'date_divider'}
  <p class="date-divider"><span>{formatDate(item.content.timestamp)}</span></p>
{:else if item.content.kind === 'timeline_start'}
  <p class="separator">{i18n.t('timeline.start')}</p>
{:else if item.content.kind === 'read_marker'}
  {#if unreadCount > 0}
    <p class="unread">
      <span>{i18n.t('timeline.unreadCount', { count: unreadCount })}</span>
    </p>
  {:else}
    <p class="read-marker"><span>{i18n.t('timeline.readMarker')}</span></p>
  {/if}
{/if}

<style>
  .separator,
  .unread,
  .date-divider,
  .state,
  .debug-event {
    margin: 0;
  }

  .separator {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    padding: var(--space-200);
    text-align: center;
  }

  .state {
    align-items: first baseline;
    color: var(--surface-var-on-container);
    display: flex;
    font-size: var(--font-size-body);
    gap: var(--timeline-row-gap);
    line-height: var(--line-height-body);
    padding: 0;
  }

  .state-icon,
  .state :global(.state-event-text),
  .state-text {
    opacity: var(--opacity-p300);
  }

  .state-icon {
    align-items: center;
    align-self: start;
    display: flex;
    flex: 0 0 var(--avatar-size-small);
    justify-content: center;
    translate: 0 var(--space-100);
  }

  .state-icon :global(svg) {
    height: var(--icon-size-small);
    width: var(--icon-size-small);
  }

  .state-time {
    color: var(--surface-var-on-container);
    flex-shrink: 0;
    font-size: var(--font-size-small);
    margin-inline-start: auto;
    white-space: nowrap;
  }

  .state :global(.state-event-text),
  .state-text {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .state-detail {
    display: grid;
    gap: var(--space-050);
    margin-inline-start: calc(var(--avatar-size-small) + var(--timeline-row-gap));
    min-width: 0;
    opacity: var(--opacity-p300);
    padding-block: var(--space-050);
  }

  .state-detail.target-above,
  .state-detail:has(:global(.target-connected)) {
    padding-block: 0;
  }

  .state :global(.state-emote) {
    display: inline-block;
    height: 1lh;
    object-fit: contain;
    vertical-align: bottom;
    width: calc(1lh * var(--media-ratio));
  }

  .redacted-label {
    align-items: center;
    border: var(--border-width) dashed var(--surface-var-container-line);
    border-radius: var(--radius-pill);
    display: inline-flex;
    gap: var(--space-100);
    padding: var(--space-050) var(--space-200);
  }

  .debug-event {
    align-items: baseline;
    background: var(--surface-var-container);
    border-block: var(--border-width) dashed var(--surface-var-container-line);
    color: var(--surface-var-on-container);
    display: flex;
    font-size: var(--font-size-small);
    gap: var(--space-200);
    padding: var(--space-150) 0;
  }

  .debug-body {
    align-items: baseline;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: var(--space-050) var(--space-200);
    min-width: 0;
  }

  .debug-body :global(.debug-peek-root) {
    display: contents;
  }

  .debug-body :global(.debug-peek-content) {
    flex: 0 0 100%;
    min-width: 0;
  }

  .debug-body :global(.debug-peek-trigger) {
    background: none;
    border: 0;
    color: var(--primary-main);
    cursor: pointer;
    font: inherit;
    font-size: var(--font-size-small);
    justify-self: start;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .debug-peek {
    background: var(--bg-container);
    border-radius: var(--radius);
    font-size: var(--font-size-small);
    margin: var(--space-100) 0 0;
    max-height: 14rem;
    overflow: auto;
    padding: var(--space-200);
  }

  .debug-event code {
    flex: 0 0 18ch;
    font-family: var(--font-family-mono);
    font-variant-ligatures: none;
    margin-inline-start: calc(var(--avatar-size-small) + var(--space-250));
    overflow-wrap: anywhere;
    text-align: end;
  }

  .date-divider {
    align-items: center;
    color: var(--surface-var-on-container);
    display: flex;
    font-size: var(--font-size-small);
    gap: var(--space-300);
    padding: var(--space-300) 0;
    text-align: center;
  }

  .date-divider::before,
  .date-divider::after {
    border-top: var(--border-width) solid var(--bg-container-line);
    content: '';
    flex: 1;
  }

  .date-divider span {
    font-weight: var(--font-weight-500);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .unread,
  .read-marker {
    align-items: center;
    display: flex;
    gap: var(--space-200);
    margin: 0;
    padding: var(--space-100) 0;
  }

  .read-marker::before {
    border-top: var(--border-width) solid var(--success-main);
    content: '';
    flex: 1;
  }

  .read-marker span {
    color: var(--success-main);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .unread::before {
    border-top: calc(var(--border-width) * 2) solid var(--primary-main-line);
    content: '';
    flex: 1;
  }

  .unread span {
    background: var(--primary-container);
    border: var(--border-width) solid var(--primary-container-line);
    border-radius: var(--radius-pill);
    color: var(--primary-on-container);
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.04em;
    padding: var(--space-050) var(--space-200);
  }
</style>
